import React, { useEffect, useState } from 'react';
import { AuthProvider } from '@vp/digital-auth-lib-ubik';
import { LocaleProvider } from '@vp/digital-locale-lib-ubik';
import { useIdentity } from '@vp/auth-react';
import { TouchBackend } from 'react-dnd-touch-backend';
import { DndProvider } from 'react-dnd';
import AppContainer from './AppContainer';
import { RuntimeContextProvider, getClientEnvironment } from '@vp/digital-environment-lib-ubik';
import OnboardingContext from './utils/OnboardingContext';
import Confetti from './ui/Confetti';
import CustomDragLayer from './ui/dnd/CustomDragLayer';
import BookendsHeader from './ui/BookendsHeader';
import { SessionProvider } from './hooks/SessionContext';
import AppContent from './AppContent';
import { AnalyticsProvider } from './hooks/AnalyticsProvider';
import NewRelicErrorBoundary from './hooks/NewRelicErrorBoundary';
import CareAgentBookendsHeader from './ui/CareAgentBookendsHeader';
import { AssetStoreProvider } from './hooks/AssetStoreProvider';

import './App.scss';

import { Helmet } from 'react-helmet';

import { SwanProvider, getRootClassNames, SwanHead, SWAN_STYLE_KEY_MAP } from '@vp/swan';

import { loadSwanTranslations, toSwanLocale } from '../swanI18n';

const styleSheetKeys = [
  SWAN_STYLE_KEY_MAP.core,
  SWAN_STYLE_KEY_MAP.spinner,
  SWAN_STYLE_KEY_MAP.grid,
  SWAN_STYLE_KEY_MAP.utility,
  SWAN_STYLE_KEY_MAP.standardTile,
  SWAN_STYLE_KEY_MAP.selectionSet,
  SWAN_STYLE_KEY_MAP.tabs,
  SWAN_STYLE_KEY_MAP.modalDialog,
  SWAN_STYLE_KEY_MAP.alertBox,
  SWAN_STYLE_KEY_MAP.banner,
];

export const SwanConfiguration: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const bodyClassName = getRootClassNames();
  // const htmlClassName = useBrowserClasses();
  const [swanI18n, setSwanI18n] = useState<{
    swanLocale: string;
    translations: Record<string, string>;
  } | null>(null);

  useEffect(() => {
    let active = true;
    const swanLocale = toSwanLocale(window.csbData.language);
    loadSwanTranslations(swanLocale).then((translations) => {
      if (active) {
        setSwanI18n({ swanLocale, translations });
      }
    });
    return () => {
      active = false;
    };
  }, []);

  return (
    <SwanProvider swanLocale={swanI18n?.swanLocale} translations={swanI18n?.translations}>
      {/* <Helmet bodyAttributes={{ class: bodyClassName }} htmlAttributes={{ class: htmlClassName }} /> */}
      <Helmet bodyAttributes={{ class: bodyClassName }} />
      <SwanHead renderStyleContentAsChildren styleSheetKeys={styleSheetKeys} />
      {/* Optionally, if you need, wrap ScreenProvider at this level */}
      {children}
    </SwanProvider>
  );
};

const App: React.FC = () => {
  const [onboardingStep, setOnboardingStep] = useState<number>(0);
  const [backButtonDisabled, setBackButtonDisabled] = useState<boolean>(false);
  const [confetti, setConfetti] = useState<boolean>(false);
  const identity = useIdentity();
  const loggedInAsCareAgent = identity.profile['https://claims.cimpress.io/cimpress_internal'];
  const boom = () => {
    setConfetti(true);
    setTimeout(() => {
      setConfetti(false);
    }, 500);
  };

  const nextOnboardingStep = () => {
    setOnboardingStep(onboardingStep + 1);
  };

  return (
    <NewRelicErrorBoundary>
      <LocaleProvider rawLocaleIdentifier={window.csbData.language}>
        <RuntimeContextProvider getEnvironment={getClientEnvironment}>
          <AuthProvider directAccessClientId={window.vbcConfig.auth0ClientId}>
            <SwanConfiguration>
              <SessionProvider>
                <OnboardingContext.Provider
                  value={{ setBackButtonDisabled, backButtonDisabled, nextOnboardingStep }}
                >
                  <AnalyticsProvider>
                    <AssetStoreProvider>
                      <Confetti enable={confetti} />
                      {loggedInAsCareAgent && <CareAgentBookendsHeader />}
                      <BookendsHeader boom={boom} />
                      <DndProvider backend={TouchBackend} options={{ enableMouseEvents: true }}>
                        <CustomDragLayer />
                        <AppContainer>
                          <AppContent boom={boom} />
                        </AppContainer>
                      </DndProvider>
                    </AssetStoreProvider>
                  </AnalyticsProvider>
                </OnboardingContext.Provider>
              </SessionProvider>
            </SwanConfiguration>
          </AuthProvider>
        </RuntimeContextProvider>
      </LocaleProvider>
    </NewRelicErrorBoundary>
  );
};

export default App;
